import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../api/apiService';
import { SensitiveContentWrapper, ReadOnlyIndicator } from '../components/ui/PermissionWrapper';
import { usePermissions } from '../hooks/usePermissions';

// Helper function to check if an array is empty
const isEmpty = (arr) => !arr || arr.length === 0;

const ApiManager = () => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  
  // Check if user has manage access vs read-only access
  const canManageApiEndpoints = permissions.canManage('apiEndpoints');
  const canViewApiEndpoints = permissions.canView('apiEndpoints');
  const hasReadOnlyAccess = canViewApiEndpoints && !canManageApiEndpoints;
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [sessionStartEndpoint, setSessionStartEndpoint] = useState(null);
  const [isSessionStart, setIsSessionStart] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [endpointType, setEndpointType] = useState('external');
  const [authType, setAuthType] = useState('none');
  const [bodyType, setBodyType] = useState('none');
  const [parameters, setParameters] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [bodyFields, setBodyFields] = useState([]);
  const [responseMappings, setResponseMappings] = useState([]);
  const [errorResponseMappings, setErrorResponseMappings] = useState([]);
  // Add state for auth fields
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authValue, setAuthValue] = useState('');
  const [authKey, setAuthKey] = useState('');
  const [getFromSdk, setGetFromSdk] = useState(false);
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [apiName, setApiName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [pathVariables, setPathVariables] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showSessionStartAPI, setShowSessionStartAPI] = useState(false);
  // Add state for response example
  const [responseExample, setResponseExample] = useState('');
  const [isGeneratingExample, setIsGeneratingExample] = useState(false);
  
  // Sync to production state
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [endpointToSync, setEndpointToSync] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Add a function to generate unique IDs
  const generateUniqueId = () => {
    return Date.now() + Math.floor(Math.random() * 10000);
  };

  // Sync to production functionality
  const shouldShowSyncButton = () => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    console.log('[ApiManager] Environment check:', { 
      environment, 
      shouldShow: environment === 'local' || environment === 'staging' 
    });
    return environment === 'local' || environment === 'staging';
  };

  // Helper functions for managing dynamic fields
  const addParameter = (paramInLocation) => {
    const newParam = { 
      id: generateUniqueId(), 
      key: '', 
      value: '', 
      description: '', 
      type: 'string', // Default data type
      in: paramInLocation, // Where the parameter is located (query, path, etc.)
      required: false 
    };
    setParameters([...parameters, newParam]);
  };

  const updateParameter = (id, field, value) => {
    console.log(`Updating parameter ${id}, field: ${field}, value: ${value}`);
    setParameters(parameters.map(param => 
      param.id === id ? { ...param, [field]: value } : param
    ));
  };

  const removeParameter = (id) => {
    console.log("Removing Parameter: ", id);
    setParameters(parameters.filter(param => param.id !== id));
  };

  const addHeader = () => {
    const newHeader = { id: generateUniqueId(), key: '', value: '', description: '' };
    setHeaders([...headers, newHeader]);
  };

  const updateHeader = (id, field, value) => {
    setHeaders(headers.map(header => 
      header.id === id ? { ...header, [field]: value } : header
    ));
  };

  const removeHeader = (id) => {
    setHeaders(headers.filter(header => header.id !== id));
  };

  const addBodyField = () => {
    const newField = { id: generateUniqueId(), key: '', type: 'string', value: '', description: '' };
    setBodyFields([...bodyFields, newField]);
  };

  const updateBodyField = (id, field, value) => {
    setBodyFields(bodyFields.map(f => 
      f.id === id ? { ...f, [field]: value } : f
    ));
  };

  const removeBodyField = (id) => {
    setBodyFields(bodyFields.filter(f => f.id !== id));
  };

  // Add function to handle response mapping
  const addResponseMapping = () => {
    const newMapping = { 
      id: generateUniqueId(), 
      key: '', 
      path: '', 
      type: 'string',
      description: '',
      priority: 0,
      conditions: []
    };
    setResponseMappings([...responseMappings, newMapping]);
  };

  const updateResponseMapping = (id, field, value) => {
    setResponseMappings(responseMappings.map(mapping => 
      mapping.id === id ? { ...mapping, [field]: value } : mapping
    ));
  };

  const removeResponseMapping = (id) => {
    setResponseMappings(responseMappings.filter(mapping => mapping.id !== id));
  };

  const addCondition = (mappingId) => {
    const newCondition = {
      id: generateUniqueId(),
      field: '',
      operator: 'equals',
      value: ''
    };
    setResponseMappings(responseMappings.map(mapping => 
      mapping.id === mappingId 
        ? { ...mapping, conditions: [...mapping.conditions, newCondition] }
        : mapping
    ));
  };

  const updateCondition = (mappingId, conditionId, field, value) => {
    setResponseMappings(responseMappings.map(mapping => 
      mapping.id === mappingId 
        ? {
            ...mapping,
            conditions: mapping.conditions.map(condition =>
              condition.id === conditionId 
                ? { ...condition, [field]: value }
                : condition
            )
          }
        : mapping
    ));
  };

  const removeCondition = (mappingId, conditionId) => {
    setResponseMappings(responseMappings.map(mapping => 
      mapping.id === mappingId 
        ? {
            ...mapping,
            conditions: mapping.conditions.filter(condition => condition.id !== conditionId)
          }
        : mapping
    ));
  };

  // Add function to handle nextApi parameters
  const addNextApiParameter = (mappingId, nextApiIndex) => {
    const newParam = {
      id: generateUniqueId(),
      key: '',
      value: ''
    };
    
    setResponseMappings(responseMappings.map(mapping => {
      if (mapping.id === mappingId) {
        const newNextApi = [...mapping.nextApi];
        // Initialize parameters array if it doesn't exist
        if (!newNextApi[nextApiIndex].parameters) {
          newNextApi[nextApiIndex].parameters = [];
        }
        newNextApi[nextApiIndex].parameters.push(newParam);
        return { ...mapping, nextApi: newNextApi };
      }
      return mapping;
    }));
  };

  const updateNextApiParameter = (mappingId, nextApiIndex, paramId, field, value) => {
    setResponseMappings(responseMappings.map(mapping => {
      if (mapping.id === mappingId && mapping.nextApi[nextApiIndex]) {
        const newNextApi = [...mapping.nextApi];
        const parameters = newNextApi[nextApiIndex].parameters || [];
        
        newNextApi[nextApiIndex].parameters = parameters.map(param => 
          param.id === paramId ? { ...param, [field]: value } : param
        );
        
        return { ...mapping, nextApi: newNextApi };
      }
      return mapping;
    }));
  };

  const removeNextApiParameter = (mappingId, nextApiIndex, paramId) => {
    setResponseMappings(responseMappings.map(mapping => {
      if (mapping.id === mappingId && mapping.nextApi[nextApiIndex]) {
        const newNextApi = [...mapping.nextApi];
        const parameters = newNextApi[nextApiIndex].parameters || [];
        
        newNextApi[nextApiIndex].parameters = parameters.filter(param => param.id !== paramId);
        
        return { ...mapping, nextApi: newNextApi };
      }
      return mapping;
    }));
  };

  // Add functions for managing nextApi conditions
  const addNextApiCondition = (mappingId, nextApiIndex) => {
    const newCondition = {
      id: generateUniqueId(),
      field: '',
      operator: 'equals',
      value: ''
    };
    
    setResponseMappings(responseMappings.map(mapping => {
      if (mapping.id === mappingId) {
        const newNextApi = [...mapping.nextApi];
        // Initialize conditions array if it doesn't exist
        if (!newNextApi[nextApiIndex].conditions) {
          newNextApi[nextApiIndex].conditions = [];
        }
        newNextApi[nextApiIndex].conditions.push(newCondition);
        return { ...mapping, nextApi: newNextApi };
      }
      return mapping;
    }));
  };

  const updateNextApiCondition = (mappingId, nextApiIndex, conditionId, field, value) => {
    setResponseMappings(responseMappings.map(mapping => {
      if (mapping.id === mappingId && mapping.nextApi[nextApiIndex]) {
        const newNextApi = [...mapping.nextApi];
        const conditions = newNextApi[nextApiIndex].conditions || [];
        
        newNextApi[nextApiIndex].conditions = conditions.map(condition => 
          condition.id === conditionId ? { ...condition, [field]: value } : condition
        );
        
        return { ...mapping, nextApi: newNextApi };
      }
      return mapping;
    }));
  };

  const removeNextApiCondition = (mappingId, nextApiIndex, conditionId) => {
    setResponseMappings(responseMappings.map(mapping => {
      if (mapping.id === mappingId && mapping.nextApi[nextApiIndex]) {
        const newNextApi = [...mapping.nextApi];
        const conditions = newNextApi[nextApiIndex].conditions || [];
        
        newNextApi[nextApiIndex].conditions = conditions.filter(condition => condition.id !== conditionId);
        
        return { ...mapping, nextApi: newNextApi };
      }
      return mapping;
    }));
  };

  // Add function to handle error response mapping
  const addErrorResponseMapping = () => {
    const newMapping = { 
      id: generateUniqueId(), 
      key: '', 
      path: '', 
      type: 'string',
      description: '',
      errorCondition: 'status_code', // New field to specify error condition
      errorValue: '400' // Value that indicates this is an error
    };
    setErrorResponseMappings([...errorResponseMappings, newMapping]);
  };

  const updateErrorResponseMapping = (id, field, value) => {
    setErrorResponseMappings(errorResponseMappings.map(mapping => 
      mapping.id === id ? { ...mapping, [field]: value } : mapping
    ));
  };

  const removeErrorResponseMapping = (id) => {
    setErrorResponseMappings(errorResponseMappings.filter(mapping => mapping.id !== id));
  };

  // Reset form state when opening/closing form
  useEffect(() => {
    if (!isFormOpen) {
      setParameters([]);
      setHeaders([]);
      setBodyFields([]);
      setResponseMappings([]);
      setErrorResponseMappings([]);
      setEndpointType('external');
      setAuthType('none');
      setBodyType('none');
      // Reset auth fields
      setAuthUsername('');
      setAuthPassword('');
      setAuthValue('');
      setAuthKey('');
      setGetFromSdk(false);
      setMethod('GET');
      setUrl('');
      setApiName('');
      setDescription('');
      setIsPublic(false);
      setPathVariables([]);
      setIsEdit(false);
      setEditId(null);
      // Reset response example
      setResponseExample('');
    } else if (selectedEndpoint) {
      setMethod(selectedEndpoint.method || 'GET');
      setUrl(selectedEndpoint.url || '');
      setApiName(selectedEndpoint.name || '');
      setDescription(selectedEndpoint.description || '');
      setIsPublic(selectedEndpoint.isPublic !== undefined ? selectedEndpoint.isPublic : false);
      
      // Parse path variables from URL
      const pathVariables = parseUrlForPathVariables(selectedEndpoint.url || '');
      setPathVariables(pathVariables);
      
      // Set edit state
      setIsEdit(true);
      setEditId(selectedEndpoint._id);
      
      // Map parameters correctly with unique IDs
      setParameters(selectedEndpoint.parameters?.map(p => ({ 
        ...p, 
        id: generateUniqueId(),
        key: p.name || p.key, // Map name to key for frontend
        type: p.type || 'string', // Ensure type is set
        in: p.in || 'query',   // Ensure in is set
      })) || []);
      
      // Headers with unique IDs
      setHeaders(selectedEndpoint.headers?.map(h => ({ 
        ...h, 
        id: generateUniqueId() 
      })) || []);
      
      // Body fields with unique IDs 
      setBodyFields(selectedEndpoint.bodyParameters?.map(b => ({ 
        ...b, 
        id: generateUniqueId() 
      })) || []);
      
      // Set other form fields
      setEndpointType(selectedEndpoint.endpointType || 'external');
      setAuthType(selectedEndpoint.authType || 'none');
      setBodyType(selectedEndpoint.bodyType || 'none');
      
      // Set auth fields
      setAuthUsername(selectedEndpoint.authUsername || '');
      setAuthPassword(selectedEndpoint.authPassword || '');
      setAuthValue(selectedEndpoint.authValue || '');
      setAuthKey(selectedEndpoint.authKey || '');
      
      // Only set getFromSdk to true if the auth type supports it
      const authType = selectedEndpoint.authType || 'none';
      const supportsGetFromSdk = ['bearer', 'jwt', 'apiKey'].includes(authType);
      setGetFromSdk(supportsGetFromSdk && (selectedEndpoint.getFromSdk || false));

      // Set response example if it exists
      if (selectedEndpoint.responseExample) {
        setResponseExample(
          typeof selectedEndpoint.responseExample === 'string' 
            ? selectedEndpoint.responseExample 
            : JSON.stringify(selectedEndpoint.responseExample, null, 2)
        );
      } else {
        setResponseExample('');
      }

      // Load response mappings with proper checks
      if (selectedEndpoint.responseMapping) {
        let mappings = [];
        try {
          // Handle both Map and object formats
          if (selectedEndpoint.responseMapping instanceof Map) {
            mappings = Array.from(selectedEndpoint.responseMapping.entries()).map(([key, value]) => ({
              id: generateUniqueId(),
              key,
              path: value.path,
              type: value.type,
              description: value.description,
              priority: value.priority,
              conditions: value.conditions.map(condition => ({
                id: generateUniqueId(),
                ...condition
              })),
              // Include nextApi if it exists
              nextApi: Array.isArray(value.nextApi) ? value.nextApi.map(nextStep => ({
                api: nextStep.api,
                parameters: Array.isArray(nextStep.parameters) ? nextStep.parameters.map(param => ({
                  id: generateUniqueId(),
                  key: param.key,
                  value: param.value
                })) : [],
                conditions: (nextStep.conditions || []).map(condition => ({
                  id: generateUniqueId(),
                  ...condition
                })),
                // Include delay configuration
                delayApiCall: nextStep.delayApiCall || false,
                delayMs: nextStep.delayMs || 1000
              })) : []
            }));
          } else if (typeof selectedEndpoint.responseMapping === 'object') {
            // Handle plain object format
            mappings = Object.entries(selectedEndpoint.responseMapping).map(([key, value]) => ({
              id: generateUniqueId(),
              key,
              path: value.path,
              type: value.type,
              description: value.description,
              priority: value.priority,
              conditions: value.conditions.map(condition => ({
                id: generateUniqueId(),
                ...condition
              })),
              // Include nextApi if it exists
              nextApi: Array.isArray(value.nextApi) ? value.nextApi.map(nextStep => ({
                api: nextStep.api,
                parameters: Array.isArray(nextStep.parameters) ? nextStep.parameters.map(param => ({
                  id: generateUniqueId(),
                  key: param.key,
                  value: param.value
                })) : [],
                conditions: (nextStep.conditions || []).map(condition => ({
                  id: generateUniqueId(),
                  ...condition
                })),
                // Include delay configuration
                delayApiCall: nextStep.delayApiCall || false,
                delayMs: nextStep.delayMs || 1000
              })) : []
            }));
          }
        } catch (error) {
          console.error('Error loading response mappings:', error);
          // Set empty mappings if there's an error
          mappings = [];
        }
        setResponseMappings(mappings);
      } else {
        setResponseMappings([]);
      }

      // Load error response mappings with proper checks
      if (selectedEndpoint.errorResponseMapping) {
        let errorMappings = [];
        try {
          if (Array.isArray(selectedEndpoint.errorResponseMapping)) {
            errorMappings = selectedEndpoint.errorResponseMapping.map(mapping => ({
              id: generateUniqueId(),
              key: mapping.key,
              path: mapping.path,
              type: mapping.type || 'string',
              description: mapping.description || '',
              errorCondition: mapping.errorCondition || 'always',
              errorValue: mapping.errorValue || ''
            }));
          }
        } catch (error) {
          console.error('Error loading error response mappings:', error);
          errorMappings = [];
        }
        setErrorResponseMappings(errorMappings);
      } else {
        setErrorResponseMappings([]);
      }

      console.log("Selected Endpoint: ", selectedEndpoint);
    }
  }, [isFormOpen, selectedEndpoint]);

  // Fetch API endpoints with debugging
  const { data: endpoints = [], isLoading, isError, error: fetchError } = useQuery({
    queryKey: ['apiEndpoints', projectId],
    queryFn: async () => {
      try {
        console.log(`Fetching API endpoints for project: ${projectId}`);
        const result = await apiService.getApiEndpoints(projectId);
        console.log('API endpoints response:', result);
        return result || [];
      } catch (err) {
        console.error('Error fetching API endpoints:', err);
        setDebugInfo({
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
        throw err;
      }
    },
    retry: 1
  });

  // Log endpoints data whenever it changes
  useEffect(() => {
    console.log('Current endpoints data:', endpoints);
  }, [endpoints]);

  // Fetch project settings to get session start endpoint
  const { data: projectSettings, refetch: refetchProjectSettings } = useQuery({
    queryKey: ['projectSettings', projectId],
    queryFn: async () => {
      console.log('Executing getProjectSettings query function');
      const result = await apiService.getProjectSettings(projectId);
      console.log('Project settings from query function:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('Project settings loaded in onSuccess:', data);
      if (data?.sessionStartEndpointId) {
        console.log('Found sessionStartEndpointId:', data.sessionStartEndpointId);
        setSessionStartEndpoint(data.sessionStartEndpointId);
        
        // If we're editing the session start endpoint, set the checkbox
        if (selectedEndpoint && selectedEndpoint._id && 
            selectedEndpoint._id.toString() === data.sessionStartEndpointId.toString()) {
          setIsSessionStart(true);
        }
      } else {
        console.log('No sessionStartEndpointId found in project settings');
        setSessionStartEndpoint(null);
      }
    },
    // Force a fresh fetch on mount
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true
  });

  // Ensure we have project settings data
  useEffect(() => {
    if (projectSettings) {
      console.log('Project settings detected in effect:', projectSettings);
      if (projectSettings.sessionStartEndpointId) {
        console.log('Setting sessionStartEndpoint from effect:', projectSettings.sessionStartEndpointId);
        setSessionStartEndpoint(projectSettings.sessionStartEndpointId);
        
        // If we're editing the session start endpoint, set the checkbox
        if (selectedEndpoint && selectedEndpoint._id && 
            selectedEndpoint._id.toString() === projectSettings.sessionStartEndpointId.toString()) {
          setIsSessionStart(true);
        }
      }
      else
      {
        console.log("Project Setting sessionStartEndpointID is null");
        setSessionStartEndpoint(null);
        updateSessionStartEndpoint(null);
      }
    }
  }, [projectSettings, selectedEndpoint]);

  // Add/Update endpoint mutation
  const { mutate: saveEndpoint, isLoading: isSaving } = useMutation({
    mutationFn: (data) => {
      console.log('Saving endpoint with data:', data);
      if (selectedEndpoint) {
        return apiService.updateApiEndpoint(projectId, selectedEndpoint._id, data);
      }
      return apiService.createApiEndpoint(projectId, data);
    },
    onSuccess: (data) => {
      console.log('Endpoint saved successfully:', data);
      queryClient.invalidateQueries(['apiEndpoints', projectId]);
      
      // If the "Set as Session Start API" checkbox is checked, update the project settings
      if (isSessionStart) {
        updateSessionStartEndpoint(data._id);
      } else if (selectedEndpoint && sessionStartEndpoint === selectedEndpoint._id) {
        // If we're editing the current session start endpoint and the checkbox is unchecked,
        // remove it as the session start endpoint
        updateSessionStartEndpoint(null);
      }
      
      setIsFormOpen(false);
      setSelectedEndpoint(null);
      setIsSessionStart(false);
      setError(null);
    },
    onError: (err) => {
      console.error('Error saving endpoint:', err);
      setError(err.response?.data?.message || 'Failed to save API endpoint. Please check the form and try again.');
    }
  });

  // Delete endpoint mutation
  const { mutate: deleteEndpoint } = useMutation({
    mutationFn: (endpointId) => apiService.deleteApiEndpoint(projectId, endpointId),
    onSuccess: (_, endpointId) => {
      // If we're deleting the session start endpoint, remove it from project settings
      if (sessionStartEndpoint === endpointId) {
        updateSessionStartEndpoint(null);
      }
      
      queryClient.invalidateQueries(['apiEndpoints', projectId]);
    },
  });

  // Update session start endpoint mutation
  const { mutate: updateSessionStartEndpoint } = useMutation({
    mutationFn: (endpointId) => apiService.updateProjectSettings(projectId, { sessionStartEndpointId: endpointId }),
    onSuccess: () => {
      queryClient.invalidateQueries(['projectSettings', projectId]);
      refetchProjectSettings();
    },
  });

  // Update the URL input handler
  const handleUrlChange = (e) => {
    const url = e.target.value;
    // Update the URL in the form
    e.target.value = url;
    
    // Parse URL for path variables
    const pathVariables = parseUrlForPathVariables(url);
    
    // Add new path variables to parameters
    const existingPathVars = parameters.filter(p => p.in === 'path').map(p => p.key);
    const newPathVars = pathVariables.filter(p => !existingPathVars.includes(p.key));
    
    if (newPathVars.length > 0) {
      setParameters([...parameters, ...newPathVars]);
    }
  };

  // Update the parseUrlForPathVariables function to use unique IDs
  const parseUrlForPathVariables = (url) => {
    const pathVariables = [];
    const matches = url.match(/\{([^}]+)\}/g) || [];
    
    matches.forEach(match => {
      const varName = match.slice(1, -1); // Remove { and }
      if (!pathVariables.some(p => p.key === varName)) {
        pathVariables.push({
          id: generateUniqueId(),
          key: varName,
          value: '',
          description: `Path variable for ${varName}`,
          type: 'string', // Data type
          in: 'path', // Location type
          required: true
        });
      }
    });
    
    return pathVariables;
  };

  // Add mutation for generating response example
  const { mutate: generateResponseExample } = useMutation({
    mutationFn: () => {
      setIsGeneratingExample(true);
      return apiService.generateResponseExample(projectId, editId || selectedEndpoint?._id);
    },
    onSuccess: (data) => {
      console.log('Generated response example:', data);
      // Format the response example for display
      const exampleData = data.data?.data || data.data;
      setResponseExample(JSON.stringify(exampleData, null, 2));
      setIsGeneratingExample(false);
    },
    onError: (err) => {
      console.error('Error generating response example:', err);
      setError(err.response?.data?.message || 'Failed to generate response example');
      setIsGeneratingExample(false);
    }
  });

  // Sync handler functions
  const handleSyncToProduction = (endpoint) => {
    console.log('[ApiManager] Sync button clicked for endpoint:', endpoint);
    console.log('[ApiManager] Setting modal state...');
    
    setEndpointToSync(endpoint);
    setShowSyncModal(true);
    setSyncResult(null);
    setError(null);
    
    console.log('[ApiManager] Modal state should be set to true');
  };

  const handleConfirmSync = async () => {
    if (!endpointToSync) return;

    setSyncLoading(true);
    setError(null);

    try {
      const response = await apiService.syncApiEndpointToProduction(projectId, endpointToSync._id);

      setSyncResult(response.data);
      console.log('API endpoint synced successfully:', response.data);
      
      // Auto-close modal after 3 seconds on success
      setTimeout(() => {
        handleCancelSync();
      }, 3000);

    } catch (error) {
      console.error('Error syncing API endpoint:', error);
      setError(error.response?.data?.message || 'Failed to sync API endpoint to production');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCancelSync = () => {
    setShowSyncModal(false);
    setEndpointToSync(null);
    setSyncResult(null);
    setError(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    const formData = new FormData(e.target);
    const endpointType = formData.get('endpointType');
    
    const data = {
      name: formData.get('name'),
      endpointType,
      description: formData.get('description'),
      responseExample: responseExample ? (
        // Try to parse as JSON if possible, otherwise use as string
        (() => {
          try {
            return JSON.parse(responseExample);
          } catch (e) {
            return responseExample;
          }
        })()
      ) : null
    };

    // Validate required fields
    if (!data.name) {
      setError('Name is required');
      return;
    }
    if (!data.description) {
      setError('Description is required');
      return;
    }

    // Handle internal API type
    if (endpointType === 'internal') {
      const internalApiType = formData.get('internalApiType');
      if (!internalApiType) {
        setError('Internal API type is required');
        return;
      }
      data.internalApiType = internalApiType;

      // Set default values based on internal API type
      if (internalApiType === 'nearest') {
        data.method = 'GET';
        data.parameters = [
          {
            name: 'latitude',
            type: 'number',
            description: 'Latitude coordinate for location search',
            required: true
          },
          {
            name: 'longitude',
            type: 'number',
            description: 'Longitude coordinate for location search',
            required: true
          },
          {
            name: 'radius',
            type: 'number',
            description: 'Search radius in kilometers (optional)',
            required: false
          },
          {
            name: 'keyword',
            type: 'string',
            description: 'Optional keyword to filter locations',
            required: false
          }
        ];
      } else if (internalApiType === 'geocode') {
        data.method = 'GET';
        data.parameters = [
          {
            name: 'address',
            type: 'string',
            description: 'Address or location query to geocode',
            required: true
          },
          {
            name: 'city',
            type: 'string',
            description: 'City name to improve geocoding accuracy (optional)',
            required: false
          },
          {
            name: 'country',
            type: 'string',
            description: 'Country name to improve geocoding accuracy (optional)',
            required: false
          }
        ];
      } else if (internalApiType === 'reverse-geocode') {
        data.method = 'GET';
        data.parameters = [
          {
            name: 'latitude',
            type: 'number',
            description: 'Latitude coordinate (must be between -90 and 90)',
            required: true
          },
          {
            name: 'longitude',
            type: 'number',
            description: 'Longitude coordinate (must be between -180 and 180)',
            required: true
          }
        ];
      } 
    } 
    // Handle external API type
    else {
      const url = formData.get('url');
      if (!url) {
        setError('URL is required for external APIs');
        return;
      }
      
      data.url = url;
      data.method = formData.get('method');
      
      // Add parameters
      data.parameters = parameters.map(param => ({
        name: param.key,
        description: param.description,
        type: param.type || 'string', // Ensure data type is set
        in: param.in || 'query', // Where the parameter is located
        required: param.required
      })).filter(param => param.name);

      // Add headers
      data.headers = headers.map(header => ({
        key: header.key,
        value: header.value,
        description: header.description
      })).filter(header => header.key);

      // Handle authentication
      data.authType = authType;
      if (authType !== 'none') {
        switch (authType) {
          case 'basic':
            data.authUsername = authUsername;
            data.authPassword = authPassword;
            // Make fields optional
            break;
          
          case 'bearer':
          case 'jwt':
            data.authValue = authValue;
            data.getFromSdk = getFromSdk;
            // Make fields optional when getting from SDK
            break;
          
          case 'apiKey':
            data.authKey = authKey;
            data.authValue = authValue;
            data.getFromSdk = getFromSdk;
            // Make fields optional when getting from SDK
            break;
        }
      }

      // Handle body
      data.bodyType = bodyType;
      if (bodyType !== 'none') {
        switch (bodyType) {
          case 'form-data':
          case 'x-www-form-urlencoded':
            data.bodyParameters = bodyFields.map(field => ({
              key: field.key,
              type: field.type,
              description: field.description
            })).filter(field => field.key);
            break;
          
          case 'raw':
            data.rawBodyContentType = formData.get('rawBodyContentType');
            data.rawBodyTemplate = formData.get('rawBodyTemplate');
            if (!data.rawBodyTemplate) {
              setError('Body template is required when using raw body type');
              return;
            }
            break;
          
          case 'graphql':
            data.graphqlQuery = formData.get('graphqlQuery');
            data.graphqlVariables = formData.get('graphqlVariables');
            if (!data.graphqlQuery) {
              setError('GraphQL query is required');
              return;
            }
            break;
        }
      }
    }
    
    // Add response mappings to the data object with proper checks
    if (responseMappings.length > 0) {
      data.responseMapping = {};
      responseMappings.forEach(mapping => {
        if (!mapping.key) return; // Skip empty mappings
        
        data.responseMapping[mapping.key] = {
          path: mapping.path,
          type: mapping.type || 'string',
          description: mapping.description || '',
          priority: mapping.priority || 0,
          conditions: mapping.conditions ? mapping.conditions.map(c => ({
            field: c.field,
            operator: c.operator,
            value: c.value
          })) : []
        };
        
        // Process nextApi if present
        if (mapping.nextApi && mapping.nextApi.length > 0) {
          data.responseMapping[mapping.key].nextApi = mapping.nextApi.map(next => ({
            api: next.api,
            parameters: next.parameters ? next.parameters.map(p => ({
              key: p.key,
              value: p.value
            })).filter(p => p.key) : [],
            conditions: next.conditions ? next.conditions.map(c => ({
              field: c.field,
              operator: c.operator,
              value: c.value
            })) : [],
            // Include delay configuration
            ...(next.delayApiCall && {
              delayApiCall: next.delayApiCall,
              delayMs: next.delayMs || 1000
            })
          })).filter(next => next.api);
        }
      });
    }

    // Add error response mappings to the data object
    if (errorResponseMappings.length > 0) {
      data.errorResponseMapping = errorResponseMappings.filter(mapping => mapping.key && mapping.path).map(mapping => ({
        key: mapping.key,
        path: mapping.path,
        type: mapping.type || 'string',
        description: mapping.description || '',
        errorCondition: mapping.errorCondition || 'always',
        errorValue: mapping.errorValue || ''
      }));
    }
    
    console.log('Submitting form with data:', data);
    
    // Final safety check: ensure getFromSdk is false for auth types that don't support it
    if (!['bearer', 'jwt', 'apiKey'].includes(data.authType)) {
      data.getFromSdk = false;
    }
    
    saveEndpoint(data);
  };

  const handleSetSessionStartEndpoint = (endpointId) => {
    console.log('Setting session start endpoint:', endpointId, 'Current:', sessionStartEndpoint);
    if (sessionStartEndpoint && sessionStartEndpoint.toString() === endpointId.toString()) {
      // If clicking on the current session start endpoint, unset it
      console.log('Unsetting current session start endpoint');
      // Immediately update local state for instant UI feedback
      setSessionStartEndpoint(null);
      // Then update the server state
      updateSessionStartEndpoint(null);
    } else {
      console.log('Setting new session start endpoint');
      // Immediately update local state for instant UI feedback
      setSessionStartEndpoint(endpointId);
      // Then update the server state
      updateSessionStartEndpoint(endpointId);
    }
  };

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard-header">
          <h1 className="page-title">API Manager</h1>
          {canManageApiEndpoints && (
            <button 
              className="btn btn-primary text-smd"
              onClick={() => {
                setSelectedEndpoint(null);
                setIsSessionStart(false);
                setIsFormOpen(true);
              }}
            >
              Add New API Endpoint
            </button>
          )}
        </div>

        <div className="card mt-4 p-4">
          <h2 className="text-lg font-medium mb-4">Session Start API</h2>
          <p className="text-smd text-gray-600 mb-4">
            Select an API endpoint to be called when a chat session begins. This is useful for retrieving user information
            and providing a personalized greeting.
          </p>
          {isLoading ? (
            <p>Loading endpoints...</p>
          ) : isError ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p><strong>Error loading endpoints:</strong> {fetchError?.message}</p>
              <button 
                className="mt-2 btn btn-sm btn-primary"
                onClick={() => queryClient.invalidateQueries(['apiEndpoints', projectId])}
              >
                Retry Loading
              </button>
            </div>
          ) : isEmpty(endpoints) ? (
            <p>No API endpoints configured yet. Add one to set as session start API.</p>
          ) : (
            <div className="space-y-2">

              {showSessionStartAPI ? (
                <>
                  <button className="arrowBtn w-full" onClick={ () => {setShowSessionStartAPI(false)}}><p className="arrow upArrow"></p></button>
                  {endpoints.map((endpoint) => {
                  // First prioritize the local state, then fall back to server state
                  const currentSessionStartId = sessionStartEndpoint || (projectSettings?.sessionStartEndpointId || null);
                  
                  // Ensure we compare strings to avoid object reference comparison issues
                  // Only consider it selected if currentSessionStartId has a value AND matches this endpoint
                  const isSelected = Boolean(currentSessionStartId) && 
                                  endpoint._id && 
                                  currentSessionStartId.toString() === endpoint._id.toString();
                  
                  console.log(`Endpoint ${endpoint.name} (${endpoint._id}) selected as session start:`, 
                    isSelected, 
                    `local state: ${sessionStartEndpoint}, server state: ${projectSettings?.sessionStartEndpointId}`
                  );
                  
                  return (
                    <div 
                      key={endpoint._id || Math.random().toString()}
                      className={`border rounded p-3 cursor-pointer hover:bg-gray-50 ${isSelected ? 'border-blue-500 bg-blue-50' : ''}`}
                      onClick={() => handleSetSessionStartEndpoint(endpoint._id)}
                    >
                      <div className="flex items-center">
                        <input 
                          type="radio" 
                          checked={isSelected}
                          onChange={() => {}}
                          className="mr-2"
                        />
                        <div>
                          <h3 className="font-medium ml-2">{endpoint.name}</h3>
                          <div className="flex space-x-2 text-xs text-gray-500 ml-2 pb-4">
                            <span>{endpoint.method}</span>
                            <span>{endpoint.url}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                </>
               ) : (
                 <>
                   <button className="arrowBtn w-full"  onClick={ () => {setShowSessionStartAPI(true)}}><p className="arrow downArrow"></p></button>
                 </>
               )}
              
            </div>
          )}
        </div>

        {isFormOpen && (
          <div className="card mt-4 p-4">
            <h2 className="text-lg font-medium mb-4">
              {selectedEndpoint ? 'Edit API Endpoint' : 'New API Endpoint'}
            </h2>
            
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <strong>Error:</strong> {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  defaultValue={selectedEndpoint?.name}
                  required
                  disabled={hasReadOnlyAccess}
                />
              </div>

              <div className="form-group">
                <label className="form-label">API Type</label>
                <select
                  name="endpointType"
                  className="api-manager-form-select text-smd"
                  defaultValue={selectedEndpoint?.endpointType || 'external'}
                  onChange={(e) => setEndpointType(e.target.value)}
                  disabled={hasReadOnlyAccess}
                >
                  <option value="internal">Internal API</option>
                  <option value="external">External API</option>
                </select>
              </div>

              {endpointType === 'internal' ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Internal API Type</label>
                    <select
                      name="internalApiType"
                      className="api-manager-form-select text-smd"
                      defaultValue={selectedEndpoint?.internalApiType || 'nearest'}
                      required
                    >
                      <option value="nearest">Find Nearest Locations</option>
                      <option value="geocode">Geocode Address</option>
                      <option value="reverse-geocode">Reverse Geocode Coordinates</option>
                    </select>
                    <p className="text-sm text-gray-500 mt-1">
                      Internal APIs are pre-configured endpoints that are optimized for common location-based operations.
                    </p>
                  </div>

                  {/* Response Mapping Section */}
                  <div className="form-section mt-6">
                    <h3 className="text-lg font-medium mb-4">Response Mapping</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Define how to map API response fields to variables. This helps in extracting and using specific fields from the response.
                    </p>
                    <div className="space-y-4">
                      {responseMappings.map((mapping) => (
                        <div key={mapping.id} className="border rounded p-4 mb-4">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="form-label">Variable Name</label>
                              <input
                                type="text"
                                className="form-input m-w-250px"
                                value={mapping.key}
                                onChange={(e) => updateResponseMapping(mapping.id, 'key', e.target.value)}
                                placeholder="e.g., clinicID"
                                disabled={hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                            <div>
                              <label className="form-label">JSON Path</label>
                              <input
                                type="text"
                                className="form-input m-w-250px"
                                value={mapping.path}
                                onChange={(e) => updateResponseMapping(mapping.id, 'path', e.target.value)}
                                placeholder="e.g., data[*].id"
                                disabled={hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                            <div>
                              <label className="form-label">Data Type</label>
                              <select
                                className="api-manager-form-select text-smd"
                                value={mapping.type}
                                onChange={(e) => updateResponseMapping(mapping.id, 'type', e.target.value)}
                                disabled={hasReadOnlyAccess}
                              >
                                <option value="string">String</option>
                                <option value="number">Number</option>
                                <option value="boolean">Boolean</option>
                                <option value="object">Object</option>
                                <option value="array">Array</option>
                              </select>
                            </div>
                            <div>
                              <label className="form-label">Priority</label>
                              <input
                                type="number"
                                className="form-input m-w-250px"
                                value={mapping.priority}
                                onChange={(e) => updateResponseMapping(mapping.id, 'priority', parseInt(e.target.value))}
                                min="0"
                                disabled={hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="form-label">Description</label>
                              <input
                                type="text"
                                className="form-input"
                                value={mapping.description}
                                onChange={(e) => updateResponseMapping(mapping.id, 'description', e.target.value)}
                                placeholder="Describe what this mapping is for"
                                disabled={hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                          </div>

                          {/* Conditions Section */}
                          <div className="mt-4">
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="text-sm font-medium">Conditions</h4>
                              {!hasReadOnlyAccess && (
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm text-smd"
                                  onClick={() => addCondition(mapping.id)}
                                >
                                  Add Condition
                                </button>
                              )}
                            </div>
                            {mapping.conditions.map((condition) => (
                              <div key={condition.id} className="flex gap-2 items-center mb-2">
                                <input
                                  type="text"
                                  className="form-input m-w-250px mr-2"
                                  value={condition.field}
                                  onChange={(e) => updateCondition(mapping.id, condition.id, 'field', e.target.value)}
                                  placeholder="Field (e.g., attributes.name)"
                                  disabled={hasReadOnlyAccess}
                                  readOnly={hasReadOnlyAccess}
                                />
                                <select
                                  className="api-manager-form-select text-smd mr-2"
                                  value={condition.operator}
                                  onChange={(e) => updateCondition(mapping.id, condition.id, 'operator', e.target.value)}
                                  disabled={hasReadOnlyAccess}
                                >
                                  <option value="equals">Equals</option>   
                                  <option value="contains">Contains</option>
                                  <option value="startsWith">Starts With</option>
                                  <option value="endsWith">Ends With</option>
                                  <option value="matches">Matches</option>
                                </select>
                                <input
                                  type="text"
                                  className="form-input m-w-250px mr-2"
                                  value={condition.value}
                                  onChange={(e) => updateCondition(mapping.id, condition.id, 'value', e.target.value)}
                                  placeholder="Value (e.g., {SEARCH_NAME})"
                                  disabled={hasReadOnlyAccess}
                                  readOnly={hasReadOnlyAccess}
                                />
                                {!hasReadOnlyAccess && (
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm text-smd"
                                    onClick={() => removeCondition(mapping.id, condition.id)}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Next API Section */}
                          <div className="mt-4 border-t pt-4">
                            <h4 className="text-sm font-medium mb-2">Next API Configuration</h4>
                            <p className="text-xs text-gray-500 mb-4">
                              Configure which APIs should be called next based on the response from this API.
                              You can add multiple next steps with different conditions.
                            </p>
                            
                            <div className="space-y-4">
                              {/* Next API Rules */}
                              <div className="space-y-4">
                                {Array.isArray(mapping.nextApi) ? mapping.nextApi.map((nextApiRule, index) => (
                                  <div key={index} className="border rounded p-4">
                                    <div className="flex justify-between items-center mb-2">
                                      <h5 className="text-smd font-medium">Next Step {index + 1}</h5>
                                      {!hasReadOnlyAccess && (
                                        <button
                                          type="button"
                                          className="btn btn-danger btn-sm text-smd"
                                          onClick={() => {
                                            const newNextApi = [...mapping.nextApi];
                                            newNextApi.splice(index, 1);
                                            updateResponseMapping(mapping.id, 'nextApi', newNextApi);
                                          }}
                                        >
                                          Remove Step
                                        </button>
                                      )}
                                    </div>
                                    
                                    <div className="space-y-2">
                                      <div>
                                        <label className="form-label">Next API</label>
                                        <select
                                          className="api-manager-form-select text-smd w-full"
                                          value={nextApiRule.api}
                                          onChange={(e) => {
                                            const newNextApi = [...mapping.nextApi];
                                            newNextApi[index] = { ...nextApiRule, api: e.target.value };
                                            updateResponseMapping(mapping.id, 'nextApi', newNextApi);
                                          }}
                                          disabled={hasReadOnlyAccess}
                                        >
                                          <option value="">Select API...</option>
                                          {endpoints.map(endpoint => (
                                            <option key={endpoint._id} value={endpoint.name}>
                                              {endpoint.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>

                                      {/* Parameters for this next API */}
                                      <div className="mt-4">
                                        <label className="form-label">Parameters</label>
                                        <p className="text-xs text-gray-500 mb-2">
                                          Define parameters to pass to the next API. These can include values from the current API response using {"{placeholders}"}.
                                        </p>
                                        
                                        <div className="space-y-2 mt-2">
                                          {(nextApiRule.parameters || []).map((param, paramIndex) => (
                                            <div key={param.id} className="flex gap-2 items-center mb-4">
                                              <input
                                                type="text"
                                                className="form-input flex-1 m-w-250px mr-2"
                                                placeholder="Parameter key"
                                                value={param.key}
                                                onChange={(e) => updateNextApiParameter(mapping.id, index, param.id, 'key', e.target.value)}
                                                disabled={hasReadOnlyAccess}
                                                readOnly={hasReadOnlyAccess}
                                              />
                                              <input
                                                type="text"
                                                className="form-input flex-1  m-w-250px mr-2"
                                                placeholder="Parameter value"
                                                value={param.value}
                                                onChange={(e) => updateNextApiParameter(mapping.id, index, param.id, 'value', e.target.value)}
                                                disabled={hasReadOnlyAccess}
                                                readOnly={hasReadOnlyAccess}
                                              />
                                              {!hasReadOnlyAccess && (
                                                <button
                                                  type="button"
                                                  className="btn btn-danger btn-xs text-smd"
                                                  onClick={() => removeNextApiParameter(mapping.id, index, param.id)}
                                                >
                                                  Remove
                                                </button>
                                              )}
                                            </div>
                                          ))}
                                          
                                          {!hasReadOnlyAccess && (
                                            <button
                                              type="button"
                                              className="btn btn-secondary btn-xs text-smd"
                                              onClick={() => addNextApiParameter(mapping.id, index)}
                                            >
                                              Add Parameter
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Conditions for this next API */}
                                      <div className="mt-4">
                                        <label className="form-label">Conditions</label>
                                        <p className="text-xs text-gray-500 mb-2">
                                          Define conditions that must be met for this next API to be called. If left empty, the API will always be called.
                                        </p>
                                        
                                        <div className="space-y-2 mt-2">
                                          {(nextApiRule.conditions || []).map((condition, conditionIndex) => (
                                            <div key={condition.id} className="flex gap-2 items-center mb-4">
                                              <input
                                                type="text"
                                                className="form-input flex-1 m-w-250px mr-2"
                                                placeholder="Field (e.g., data.status)"
                                                value={condition.field}
                                                onChange={(e) => updateNextApiCondition(mapping.id, index, condition.id, 'field', e.target.value)}
                                                disabled={hasReadOnlyAccess}
                                                readOnly={hasReadOnlyAccess}
                                              />
                                              <select
                                                className="api-manager-form-select text-smd mr-2"
                                                value={condition.operator}
                                                onChange={(e) => updateNextApiCondition(mapping.id, index, condition.id, 'operator', e.target.value)}
                                                disabled={hasReadOnlyAccess}
                                              >
                                                <option value="equals">Equals</option>
                                                <option value="contains">Contains</option>
                                                <option value="notEquals">Not Equals</option>
                                                <option value="startsWith">Starts With</option>
                                                <option value="endsWith">Ends With</option>
                                                <option value="matches">Matches</option>
                                              </select>
                                              <input
                                                type="text"
                                                className="form-input flex-1 m-w-250px mr-2"
                                                placeholder="Value"
                                                value={condition.value}
                                                onChange={(e) => updateNextApiCondition(mapping.id, index, condition.id, 'value', e.target.value)}
                                                disabled={hasReadOnlyAccess}
                                                readOnly={hasReadOnlyAccess}
                                              />
                                              {!hasReadOnlyAccess && (
                                                <button
                                                  type="button"
                                                  className="btn btn-danger btn-xs text-smd"
                                                  onClick={() => removeNextApiCondition(mapping.id, index, condition.id)}
                                                >
                                                  Remove
                                                </button>
                                              )}
                                            </div>
                                          ))}
                                          
                                          {!hasReadOnlyAccess && (
                                            <button
                                              type="button"
                                              className="btn btn-secondary btn-xs text-smd"
                                              onClick={() => addNextApiCondition(mapping.id, index)}
                                            >
                                              Add Condition
                                            </button>
                                          )}
                                        </div>
                                      </div>

                                      {/* Delay Configuration */}
                                      <div className="mt-4">
                                        <label className="form-label flex items-center">
                                          <input
                                            type="checkbox"
                                            className="form-checkbox mr-2"
                                            checked={nextApiRule.delayApiCall || false}
                                            onChange={(e) => {
                                              const newNextApi = [...mapping.nextApi];
                                              newNextApi[index] = { 
                                                ...nextApiRule, 
                                                delayApiCall: e.target.checked,
                                                // Reset delay value if unchecked
                                                delayMs: e.target.checked ? (nextApiRule.delayMs || 1000) : undefined
                                              };
                                              updateResponseMapping(mapping.id, 'nextApi', newNextApi);
                                            }}
                                            disabled={hasReadOnlyAccess}
                                          />
                                          <span className="text-sm font-medium">Delay API call?</span>
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1">
                                          Add a delay before calling the next API. Useful for rate limiting or timing sensitive operations.
                                        </p>
                                        
                                        {nextApiRule.delayApiCall && (
                                          <div className="mt-2">
                                            <label className="form-label">Delay (milliseconds)</label>
                                            <input
                                              type="number"
                                              className="form-input"
                                              placeholder="Enter delay in milliseconds"
                                              min="0"
                                              max="30000"
                                              value={nextApiRule.delayMs || 1000}
                                              onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                const clampedValue = Math.min(Math.max(value || 0, 0), 30000);
                                                const newNextApi = [...mapping.nextApi];
                                                newNextApi[index] = { 
                                                  ...nextApiRule, 
                                                  delayMs: clampedValue
                                                };
                                                updateResponseMapping(mapping.id, 'nextApi', newNextApi);
                                              }}
                                              disabled={hasReadOnlyAccess}
                                              readOnly={hasReadOnlyAccess}
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                              Maximum delay is 30 seconds (30000ms). Current: {nextApiRule.delayMs || 1000}ms ({((nextApiRule.delayMs || 1000) / 1000).toFixed(1)}s)
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )) : null}

                                {!hasReadOnlyAccess && (
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm text-smd mt-2"
                                    onClick={() => {
                                      const currentNextApi = Array.isArray(mapping.nextApi) ? mapping.nextApi : [];
                                      const newNextApi = [...currentNextApi, {
                                        api: '',
                                        parameters: [],
                                        conditions: []
                                      }];
                                      updateResponseMapping(mapping.id, 'nextApi', newNextApi);
                                    }}
                                  >
                                    Add Next Step
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {!hasReadOnlyAccess && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm mt-4 text-smd"
                              onClick={() => removeResponseMapping(mapping.id)}
                            >
                              Remove Mapping
                            </button>
                          )}
                        </div>
                      ))}
                      {!hasReadOnlyAccess && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm text-smd mb-2"
                          onClick={addResponseMapping}
                        >
                          Add Response Mapping
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Add Response Example Section - place this before the Description field */}
                  <div className="form-section mt-6">
                    <h3 className="text-lg font-medium mb-4">Response Example</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Provide an example of what the response from this API endpoint looks like. 
                      You can manually enter it or click "Generate Example" to fetch a real response.
                    </p>
                    
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-grow mr-4">
                        <textarea
                          name="responseExample"
                          className="form-textarea text-smd w-full font-mono"
                          style={{ minHeight: '200px' }}
                          value={responseExample}
                          onChange={(e) => setResponseExample(e.target.value)}
                          placeholder="Enter or generate a response example..."
                          disabled={hasReadOnlyAccess}
                          readOnly={hasReadOnlyAccess}
                        />
                      </div>
                      
                      <div>
                        {!hasReadOnlyAccess && (
                          <button
                            type="button"
                            className="btn btn-secondary text-smd"
                            onClick={() => generateResponseExample()}
                            disabled={isGeneratingExample || !isEdit}
                          >
                            {isGeneratingExample ? 'Generating...' : 'Generate Example'}
                          </button>
                        )}
                        {!isEdit && !hasReadOnlyAccess && (
                          <p className="text-xs text-gray-500 mt-2">
                            Save the endpoint first before generating an example
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      className="form-textarea w-full text-smd"
                      style={{ minHeight: '100px', width: '100%' }}
                      defaultValue={selectedEndpoint?.description}
                      required
                      placeholder="Describe when and how the LLM should use this endpoint"
                      disabled={hasReadOnlyAccess}
                      readOnly={hasReadOnlyAccess}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label flex items-center">
                      <input
                        type="checkbox"
                        checked={isSessionStart}
                        onChange={(e) => setIsSessionStart(e.target.checked)}
                        className="form-checkbox"
                        disabled={hasReadOnlyAccess}
                      />
                      <span className="">Set as Session Start API</span>
                    </label>
                    <p className="text-sm text-gray-500 mt-1 ml-6">
                      This API will be called when a chat session begins to retrieve user information.
                    </p>
                  </div>

                  {hasReadOnlyAccess && (
                    <ReadOnlyIndicator />
                  )}

                  <div className="flex gap-2">
                    {canManageApiEndpoints && (
                      <button 
                        type="submit" 
                        className="btn btn-primary text-smd"
                        disabled={isSaving}
                      >
                        {isSaving ? 'Saving...' : selectedEndpoint ? 'Update Endpoint' : 'Create Endpoint'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary text-smd ml-2"
                      onClick={() => {
                        setIsFormOpen(false);
                        setSelectedEndpoint(null);
                        setIsSessionStart(false);
                        setError(null);
                      }}
                    >
                      {canManageApiEndpoints ? 'Cancel' : 'Close'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">URL</label>
                    <input
                      type="url"
                      name="url"
                      className="form-input"
                      defaultValue={selectedEndpoint?.url}
                      required
                      onChange={handleUrlChange}
                      disabled={hasReadOnlyAccess}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Method</label>
                    <select
                      name="method"
                      className="api-manager-form-select text-smd"
                      defaultValue={selectedEndpoint?.method || 'GET'}
                      disabled={hasReadOnlyAccess}
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                      <option value="DELETE">DELETE</option>
                    </select>
                  </div>

                  {/* Parameters Section */}
                  <div className="form-section mt-6">
                    <h3 className="text-lg font-medium mb-4">Parameters</h3>
                    <div className="space-y-4">
                      {parameters.map((param) => (
                        <div key={param.id} className="flex mb-2">
                          <div className="flex-1">
                            <input
                              type="text"
                              className="form-input m-w-200px"
                              placeholder="Key"
                              value={param.key}
                              onChange={(e) => updateParameter(param.id, 'key', e.target.value)}
                              disabled={param.in === 'path' || hasReadOnlyAccess}
                            />
                          </div>
                          <div className="flex-1">
                            <select
                              className="api-manager-form-select ml-2 text-smd"
                              value={param.type || 'string'}
                              onChange={(e) => updateParameter(param.id, 'type', e.target.value)}
                              data-param-id={param.id}
                              data-param-key={param.key}
                              disabled={hasReadOnlyAccess}
                            >
                              <option value="string">String</option>
                              <option value="number">Number</option>
                              <option value="boolean">Boolean</option>
                              <option value="object">Object</option>
                              <option value="array">Array</option>
                            </select>
                          </div>
                          <div className="flex-1">
                            <input
                              type="text"
                              className="form-input m-w-200px ml-2"
                              placeholder="Description"
                              value={param.description}
                              onChange={(e) => updateParameter(param.id, 'description', e.target.value)}
                              disabled={hasReadOnlyAccess}
                            />
                          </div>
                          <div className="flex-none">
                            {canManageApiEndpoints && (
                              <button
                                type="button"
                                className="btn btn-danger ml-2 text-smd"
                                onClick={() => removeParameter(param.id)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {canManageApiEndpoints && (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm text-smd"
                            onClick={() => addParameter('query')}
                          >
                            Add Query Parameter
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm text-smd ml-2"
                            onClick={() => addParameter('path')}
                          >
                            Add Path Parameter
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Authentication Section */}
                  <SensitiveContentWrapper>
                    <div className="form-section mt-6">
                      <h3 className="text-lg font-medium mb-4">Authentication</h3>
                      <div className="form-group text-smd">
                        <select
                          name="authType"
                          className="api-manager-form-select text-smd"
                          value={authType}
                          onChange={(e) => {
                            const newAuthType = e.target.value;
                            setAuthType(newAuthType);
                            
                            // If changing to an auth type that doesn't support getFromSdk, set it to false
                            if (!['bearer', 'jwt', 'apiKey'].includes(newAuthType)) {
                              setGetFromSdk(false);
                            }
                          }}
                          disabled={hasReadOnlyAccess}
                        >
                          <option value="none">No Auth</option>
                          <option value="basic">Basic Auth</option>
                          <option value="bearer">Bearer Token</option>
                          <option value="jwt">JWT Token</option>
                          <option value="digest">Digest Auth</option>
                          <option value="oauth1">OAuth 1.0</option>
                          <option value="oauth2">OAuth 2.0</option>
                          <option value="apiKey">API Key</option>
                        </select>
                      </div>

                      {authType === 'basic' && (
                        <div className="space-y-4">
                          <div className="form-group">
                            <label className="form-label">Username</label>
                            <input
                              type="text"
                              name="authUsername"
                              className="form-input"
                              value={authUsername}
                              onChange={(e) => setAuthUsername(e.target.value)}
                              disabled={hasReadOnlyAccess}
                            />
                            <p className="text-xs text-gray-500 mt-1">Optional: Leave blank if no username is needed</p>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Password</label>
                            <input
                              type="password"
                              name="authPassword"
                              className="form-input"
                              value={authPassword}
                              onChange={(e) => setAuthPassword(e.target.value)}
                              disabled={hasReadOnlyAccess}
                            />
                            <p className="text-xs text-gray-500 mt-1">Optional: Leave blank if no password is needed</p>
                          </div>
                        </div>
                      )}

                      {(authType === 'bearer' || authType === 'jwt') && (
                        <div className="space-y-4">
                          <div className="form-group">
                            <label className="form-label flex items-center">
                              <input
                                type="checkbox"
                                name="getFromSdk"
                                className="form-checkbox"
                                checked={getFromSdk}
                                onChange={(e) => setGetFromSdk(e.target.checked)}
                                disabled={hasReadOnlyAccess}
                              />
                              <span className="ml-2">Get from SDK</span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              If checked, the token will be retrieved from the SDK integration. 
                              User must provide token in SDK setUser method.
                            </p>
                          </div>
                          {!getFromSdk && (
                            <div className="form-group">
                              <label className="form-label">Token</label>
                              <input
                                type="password"
                                name="authValue"
                                className="form-input"
                                value={authValue}
                                onChange={(e) => setAuthValue(e.target.value)}
                                disabled={getFromSdk || hasReadOnlyAccess}
                              />
                              <p className="text-xs text-gray-500 mt-1">Optional: Leave blank if getting from SDK or no token is needed</p>
                            </div>
                          )}
                        </div>
                      )}

                      {authType === 'apiKey' && (
                        <div className="space-y-4">
                          <div className="form-group">
                            <label className="form-label flex items-center">
                                                          <input
                              type="checkbox"
                              name="getFromSdk"
                              className="form-checkbox"
                              checked={getFromSdk}
                              onChange={(e) => setGetFromSdk(e.target.checked)}
                              disabled={hasReadOnlyAccess}
                            />
                            <span className="ml-2">Get from SDK</span>
                          </label>
                          <p className="text-xs text-gray-500 mt-1">
                            If checked, the API key will be retrieved from the SDK integration.
                            User must provide API key in SDK setUser method.
                          </p>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Key</label>
                          <input
                            type="text"
                            name="authKey"
                            className="form-input"
                            value={authKey}
                            onChange={(e) => setAuthKey(e.target.value)}
                            disabled={hasReadOnlyAccess}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {getFromSdk 
                              ? "This is the key name to look for in SDK user data" 
                              : "The header key name for the API key"}
                          </p>
                        </div>
                        {!getFromSdk && (
                          <div className="form-group">
                            <label className="form-label">Value</label>
                            <input
                              type="password"
                              name="authValue"
                              className="form-input"
                              value={authValue}
                              onChange={(e) => setAuthValue(e.target.value)}
                              disabled={getFromSdk || hasReadOnlyAccess}
                            />
                              <p className="text-xs text-gray-500 mt-1">Optional: Leave blank if getting from SDK or no value is needed</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </SensitiveContentWrapper>

                  {/* Headers Section */}
                  <div className="form-section mt-6">
                    <h3 className="text-lg font-medium mb-4">Headers</h3>
                    <div className="space-y-4">
                      {headers.map((header) => (
                        <div key={header.id} className="flex gap-2 items-start">
                          <div className="flex-1">
                            <input
                              type="text"
                              className="form-input mb-2 m-w-200px mr-2"
                              placeholder="Key"
                              value={header.key}
                              onChange={(e) => updateHeader(header.id, 'key', e.target.value)}
                              disabled={hasReadOnlyAccess}
                            />
                          </div>
                          <div className="flex-1">
                            <input
                              type="text"
                              className="form-input mb-2 m-w-200px mr-2"
                              placeholder="Value"
                              value={header.value}
                              onChange={(e) => updateHeader(header.id, 'value', e.target.value)}
                              disabled={hasReadOnlyAccess}
                            />
                          </div>
                          <div className="flex-1">
                            <input
                              type="text"
                              className="form-input mb-2 m-w-200px mr-2"
                              placeholder="Description"
                              value={header.description}
                              onChange={(e) => updateHeader(header.id, 'description', e.target.value)}
                              disabled={hasReadOnlyAccess}
                            />
                          </div>
                          <div className="flex-none">
                            {!hasReadOnlyAccess && (
                              <button
                                type="button"
                                className="btn btn-danger btn-sm text-smd"
                                onClick={() => removeHeader(header.id)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {!hasReadOnlyAccess && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm text-smd"
                          onClick={addHeader}
                        >
                          Add Header
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Body Section */}
                  <div className="form-section mt-6">
                    <h3 className="text-lg font-medium mb-4">Body</h3>
                    <div className="form-group">
                      <select
                        name="bodyType"
                        className="api-manager-form-select text-smd"
                        value={bodyType}
                        onChange={(e) => setBodyType(e.target.value)}
                        disabled={hasReadOnlyAccess}
                      >
                        <option value="none">None</option>
                        <option value="form-data">Form Data</option>
                        <option value="x-www-form-urlencoded">URL Encoded Form</option>
                        <option value="raw">Raw</option>
                        <option value="binary">Binary</option>
                        <option value="graphql">GraphQL</option>
                      </select>
                    </div>

                    {(bodyType === 'form-data' || bodyType === 'x-www-form-urlencoded') && (
                      <div className="space-y-4 mt-4">
                        {bodyFields.map((field) => (
                          <div key={field.id} className="flex gap-2 items-start">
                            <div className="flex-1">
                              <input
                                type="text"
                                className="form-input mb-2"
                                placeholder="Key"
                                value={field.key}
                                onChange={(e) => updateBodyField(field.id, 'key', e.target.value)}
                                disabled={hasReadOnlyAccess}
                              />
                            </div>
                            <div className="flex-1">
                              <select
                                className="api-manager-form-select text-smd mb-2"
                                value={field.type}
                                onChange={(e) => updateBodyField(field.id, 'type', e.target.value)}
                                disabled={hasReadOnlyAccess}
                              >
                                <option value="string">String</option>
                                <option value="number">Number</option>
                                <option value="boolean">Boolean</option>
                                <option value="object">Object</option>
                                <option value="array">Array</option>
                                <option value="file">File</option>
                              </select>
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                className="form-input mb-2"
                                placeholder="Description"
                                value={field.description}
                                onChange={(e) => updateBodyField(field.id, 'description', e.target.value)}
                                disabled={hasReadOnlyAccess}
                              />
                            </div>
                            <div className="flex-none">
                              {!hasReadOnlyAccess && (
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => removeBodyField(field.id)}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        {!hasReadOnlyAccess && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={addBodyField}
                          >
                            Add Field
                          </button>
                        )}
                      </div>
                    )}

                    {bodyType === 'raw' && (
                      <>
                        <div className="form-group mt-4">
                          <select
                            name="rawBodyContentType"
                            className="api-manager-form-select"
                            defaultValue={selectedEndpoint?.rawBodyContentType || 'application/json'}
                            disabled={hasReadOnlyAccess}
                          >
                            <option value="application/json">JSON</option>
                            <option value="text/plain">Plain Text</option>
                            <option value="application/xml">XML</option>
                            <option value="text/html">HTML</option>
                            <option value="application/javascript">JavaScript</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <textarea
                            name="rawBodyTemplate"
                            className="form-textarea text-smd w-full"
                            rows="8"
                            placeholder="Enter your raw body template here..."
                            defaultValue={selectedEndpoint?.rawBodyTemplate}
                            disabled={hasReadOnlyAccess}
                            readOnly={hasReadOnlyAccess}
                          />
                        </div>
                      </>
                    )}

                    {bodyType === 'graphql' && (
                      <div className="space-y-4 mt-4">
                        <div className="form-group">
                          <label className="form-label">Query</label>
                          <textarea
                            name="graphqlQuery"
                            className="form-textarea"
                            rows="8"
                            placeholder="Enter your GraphQL query here..."
                            defaultValue={selectedEndpoint?.graphqlQuery}
                            disabled={hasReadOnlyAccess}
                            readOnly={hasReadOnlyAccess}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Variables</label>
                          <textarea
                            name="graphqlVariables"
                            className="form-textarea"
                            rows="4"
                            placeholder="Enter your variables as JSON..."
                            defaultValue={selectedEndpoint?.graphqlVariables}
                            disabled={hasReadOnlyAccess}
                            readOnly={hasReadOnlyAccess}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Response Mapping Section */}
                  <div className="form-section mt-6">
                    <h3 className="text-lg font-medium mb-4">Response Mapping</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Define how to map API response fields to variables. This helps in extracting and using specific fields from the response.
                    </p>
                    <div className="space-y-4">
                      {responseMappings.map((mapping) => (
                        <div key={mapping.id} className="border rounded p-4 mb-4">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="form-label">Variable Name</label>
                              <input
                                type="text"
                                className="form-input m-w-250px"
                                value={mapping.key}
                                onChange={(e) => updateResponseMapping(mapping.id, 'key', e.target.value)}
                                placeholder="e.g., clinicID"
                                disabled={hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                            <div>
                              <label className="form-label">JSON Path</label>
                              <input
                                type="text"
                                className="form-input m-w-250px"
                                value={mapping.path}
                                onChange={(e) => updateResponseMapping(mapping.id, 'path', e.target.value)}
                                placeholder="e.g., data[*].id"
                                disabled={hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                            <div>
                              <label className="form-label">Data Type</label>
                              <select
                                className="api-manager-form-select text-smd"
                                value={mapping.type}
                                onChange={(e) => updateResponseMapping(mapping.id, 'type', e.target.value)}
                                disabled={hasReadOnlyAccess}
                              >
                                <option value="string">String</option>
                                <option value="number">Number</option>
                                <option value="boolean">Boolean</option>
                                <option value="object">Object</option>
                                <option value="array">Array</option>
                              </select>
                            </div>
                            <div>
                              <label className="form-label">Priority</label>
                              <input
                                type="number"
                                className="form-input m-w-250px"
                                value={mapping.priority}
                                onChange={(e) => updateResponseMapping(mapping.id, 'priority', parseInt(e.target.value))}
                                min="0"
                                disabled={hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="form-label">Description</label>
                              <input
                                type="text"
                                className="form-input"
                                value={mapping.description}
                                onChange={(e) => updateResponseMapping(mapping.id, 'description', e.target.value)}
                                placeholder="Describe what this mapping is for"
                                disabled={hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                          </div>

                          {/* Conditions Section */}
                          <div className="mt-4">
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="text-sm font-medium">Conditions</h4>
                              {!hasReadOnlyAccess && (
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm text-smd"
                                  onClick={() => addCondition(mapping.id)}
                                >
                                  Add Condition
                                </button>
                              )}
                            </div>
                            {mapping.conditions.map((condition) => (
                              <div key={condition.id} className="flex gap-2 items-center mb-2">
                                <input
                                  type="text"
                                  className="form-input m-w-250px mr-2"
                                  value={condition.field}
                                  onChange={(e) => updateCondition(mapping.id, condition.id, 'field', e.target.value)}
                                  placeholder="Field (e.g., attributes.name)"
                                  disabled={hasReadOnlyAccess}
                                  readOnly={hasReadOnlyAccess}
                                />
                                <select
                                  className="api-manager-form-select text-smd mr-2"
                                  value={condition.operator}
                                  onChange={(e) => updateCondition(mapping.id, condition.id, 'operator', e.target.value)}
                                  disabled={hasReadOnlyAccess}
                                >
                                  <option value="equals">Equals</option>   
                                  <option value="contains">Contains</option>
                                  <option value="startsWith">Starts With</option>
                                  <option value="endsWith">Ends With</option>
                                  <option value="matches">Matches</option>
                                </select>
                                <input
                                  type="text"
                                  className="form-input m-w-250px mr-2"
                                  value={condition.value}
                                  onChange={(e) => updateCondition(mapping.id, condition.id, 'value', e.target.value)}
                                  placeholder="Value (e.g., {SEARCH_NAME})"
                                  disabled={hasReadOnlyAccess}
                                  readOnly={hasReadOnlyAccess}
                                />
                                {!hasReadOnlyAccess && (
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm text-smd"
                                    onClick={() => removeCondition(mapping.id, condition.id)}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Next API Section */}
                          <div className="mt-4 border-t pt-4">
                            <h4 className="text-sm font-medium mb-2">Next API Configuration</h4>
                            <p className="text-xs text-gray-500 mb-4">
                              Configure which APIs should be called next based on the response from this API.
                              You can add multiple next steps with different conditions.
                            </p>
                            
                            <div className="space-y-4">
                              {/* Next API Rules */}
                              <div className="space-y-4">
                                {Array.isArray(mapping.nextApi) ? mapping.nextApi.map((nextApiRule, index) => (
                                  <div key={index} className="border rounded p-4">
                                    <div className="flex justify-between items-center mb-2">
                                      <h5 className="text-smd font-medium">Next Step {index + 1}</h5>
                                      {!hasReadOnlyAccess && (
                                        <button
                                          type="button"
                                          className="btn btn-danger btn-sm text-smd"
                                          onClick={() => {
                                            const newNextApi = [...mapping.nextApi];
                                            newNextApi.splice(index, 1);
                                            updateResponseMapping(mapping.id, 'nextApi', newNextApi);
                                          }}
                                        >
                                          Remove Step
                                        </button>
                                      )}
                                    </div>
                                    
                                    <div className="space-y-2">
                                      <div>
                                        <label className="form-label">Next API</label>
                                        <select
                                          className="api-manager-form-select text-smd w-full"
                                          value={nextApiRule.api}
                                          onChange={(e) => {
                                            const newNextApi = [...mapping.nextApi];
                                            newNextApi[index] = { ...nextApiRule, api: e.target.value };
                                            updateResponseMapping(mapping.id, 'nextApi', newNextApi);
                                          }}
                                          disabled={hasReadOnlyAccess}
                                        >
                                          <option value="">Select API...</option>
                                          {endpoints.map(endpoint => (
                                            <option key={endpoint._id} value={endpoint.name}>
                                              {endpoint.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>

                                      {/* Parameters for this next API */}
                                      <div className="mt-4">
                                        <label className="form-label">Parameters</label>
                                        <p className="text-xs text-gray-500 mb-2">
                                          Define parameters to pass to the next API. These can include values from the current API response using {"{placeholders}"}.
                                        </p>
                                        
                                        <div className="space-y-2 mt-2">
                                          {(nextApiRule.parameters || []).map((param, paramIndex) => (
                                            <div key={param.id} className="flex gap-2 items-center mb-4">
                                              <input
                                                type="text"
                                                className="form-input flex-1 m-w-250px mr-2"
                                                placeholder="Parameter key"
                                                value={param.key}
                                                onChange={(e) => updateNextApiParameter(mapping.id, index, param.id, 'key', e.target.value)}
                                                disabled={hasReadOnlyAccess}
                                                readOnly={hasReadOnlyAccess}
                                              />
                                              <input
                                                type="text"
                                                className="form-input flex-1  m-w-250px mr-2"
                                                placeholder="Parameter value"
                                                value={param.value}
                                                onChange={(e) => updateNextApiParameter(mapping.id, index, param.id, 'value', e.target.value)}
                                                disabled={hasReadOnlyAccess}
                                                readOnly={hasReadOnlyAccess}
                                              />
                                              {!hasReadOnlyAccess && (
                                                <button
                                                  type="button"
                                                  className="btn btn-danger btn-xs text-smd"
                                                  onClick={() => removeNextApiParameter(mapping.id, index, param.id)}
                                                >
                                                  Remove
                                                </button>
                                              )}
                                            </div>
                                          ))}
                                          
                                          {!hasReadOnlyAccess && (
                                            <button
                                              type="button"
                                              className="btn btn-secondary btn-xs text-smd"
                                              onClick={() => addNextApiParameter(mapping.id, index)}
                                            >
                                              Add Parameter
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Conditions for this next API */}
                                      <div className="mt-4">
                                        <label className="form-label">Conditions</label>
                                        <p className="text-xs text-gray-500 mb-2">
                                          Define conditions that must be met for this next API to be called. If left empty, the API will always be called.
                                        </p>
                                        
                                        <div className="space-y-2 mt-2">
                                          {(nextApiRule.conditions || []).map((condition, conditionIndex) => (
                                            <div key={condition.id} className="flex gap-2 items-center mb-4">
                                              <input
                                                type="text"
                                                className="form-input flex-1 m-w-250px mr-2"
                                                placeholder="Field (e.g., data.status)"
                                                value={condition.field}
                                                onChange={(e) => updateNextApiCondition(mapping.id, index, condition.id, 'field', e.target.value)}
                                                disabled={hasReadOnlyAccess}
                                                readOnly={hasReadOnlyAccess}
                                              />
                                              <select
                                                className="api-manager-form-select text-smd mr-2"
                                                value={condition.operator}
                                                onChange={(e) => updateNextApiCondition(mapping.id, index, condition.id, 'operator', e.target.value)}
                                                disabled={hasReadOnlyAccess}
                                              >
                                                <option value="equals">Equals</option>
                                                <option value="contains">Contains</option>
                                                <option value="notEquals">Not Equals</option>
                                                <option value="startsWith">Starts With</option>
                                                <option value="endsWith">Ends With</option>
                                                <option value="matches">Matches</option>
                                              </select>
                                              <input
                                                type="text"
                                                className="form-input flex-1 m-w-250px mr-2"
                                                placeholder="Value"
                                                value={condition.value}
                                                onChange={(e) => updateNextApiCondition(mapping.id, index, condition.id, 'value', e.target.value)}
                                                disabled={hasReadOnlyAccess}
                                                readOnly={hasReadOnlyAccess}
                                              />
                                              {!hasReadOnlyAccess && (
                                                <button
                                                  type="button"
                                                  className="btn btn-danger btn-xs text-smd"
                                                  onClick={() => removeNextApiCondition(mapping.id, index, condition.id)}
                                                >
                                                  Remove
                                                </button>
                                              )}
                                            </div>
                                          ))}
                                          
                                          {!hasReadOnlyAccess && (
                                            <button
                                              type="button"
                                              className="btn btn-secondary btn-xs text-smd"
                                              onClick={() => addNextApiCondition(mapping.id, index)}
                                            >
                                              Add Condition
                                            </button>
                                          )}
                                        </div>
                                      </div>

                                      {/* Delay Configuration */}
                                      <div className="mt-4">
                                        <label className="form-label flex items-center">
                                          <input
                                            type="checkbox"
                                            className="form-checkbox mr-2"
                                            checked={nextApiRule.delayApiCall || false}
                                            onChange={(e) => {
                                              const newNextApi = [...mapping.nextApi];
                                              newNextApi[index] = { 
                                                ...nextApiRule, 
                                                delayApiCall: e.target.checked,
                                                // Reset delay value if unchecked
                                                delayMs: e.target.checked ? (nextApiRule.delayMs || 1000) : undefined
                                              };
                                              updateResponseMapping(mapping.id, 'nextApi', newNextApi);
                                            }}
                                            disabled={hasReadOnlyAccess}
                                          />
                                          <span className="text-sm font-medium">Delay API call?</span>
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1">
                                          Add a delay before calling the next API. Useful for rate limiting or timing sensitive operations.
                                        </p>
                                        
                                        {nextApiRule.delayApiCall && (
                                          <div className="mt-2">
                                            <label className="form-label">Delay (milliseconds)</label>
                                            <input
                                              type="number"
                                              className="form-input"
                                              placeholder="Enter delay in milliseconds"
                                              min="0"
                                              max="30000"
                                              value={nextApiRule.delayMs || 1000}
                                              onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                const clampedValue = Math.min(Math.max(value || 0, 0), 30000);
                                                const newNextApi = [...mapping.nextApi];
                                                newNextApi[index] = { 
                                                  ...nextApiRule, 
                                                  delayMs: clampedValue
                                                };
                                                updateResponseMapping(mapping.id, 'nextApi', newNextApi);
                                              }}
                                              disabled={hasReadOnlyAccess}
                                              readOnly={hasReadOnlyAccess}
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                              Maximum delay is 30 seconds (30000ms). Current: {nextApiRule.delayMs || 1000}ms ({((nextApiRule.delayMs || 1000) / 1000).toFixed(1)}s)
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )) : null}

                                {!hasReadOnlyAccess && (
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm text-smd mt-2"
                                    onClick={() => {
                                      const currentNextApi = Array.isArray(mapping.nextApi) ? mapping.nextApi : [];
                                      const newNextApi = [...currentNextApi, {
                                        api: '',
                                        parameters: [],
                                        conditions: []
                                      }];
                                      updateResponseMapping(mapping.id, 'nextApi', newNextApi);
                                    }}
                                  >
                                    Add Next Step
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {!hasReadOnlyAccess && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm mt-4 text-smd"
                              onClick={() => removeResponseMapping(mapping.id)}
                            >
                              Remove Mapping
                            </button>
                          )}
                        </div>
                      ))}
                      {!hasReadOnlyAccess && (<button
                        type="button"
                        className="btn btn-secondary btn-sm text-smd mb-2"
                        onClick={addResponseMapping}
                      >
                        Add Response Mapping
                      </button>)
                    }
                    </div>
                  </div>

                  {/* Add Response Example Section - place this before the Description field */}
                  <div className="form-section mt-6">
                    <h3 className="text-lg font-medium mb-4">Response Example</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Provide an example of what the response from this API endpoint looks like. 
                      You can manually enter it or click "Generate Example" to fetch a real response.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="border rounded p-4">
                        <div className="flex-grow mr-4">
                          <textarea
                            name="responseExample"
                            className="form-textarea text-smd w-full font-mono"
                            style={{ minHeight: '200px' }}
                            value={responseExample}
                            onChange={(e) => setResponseExample(e.target.value)}
                            placeholder="Enter or generate a response example..."
                            disabled={hasReadOnlyAccess}
                            readOnly={hasReadOnlyAccess}
                          />
                        </div>
                        
                        <div className="mt-4">
                          {!hasReadOnlyAccess && (
                            <button
                              type="button"
                              className="btn btn-secondary text-smd"
                              onClick={() => generateResponseExample()}
                              disabled={isGeneratingExample || !isEdit}
                            >
                              {isGeneratingExample ? 'Generating...' : 'Generate Example'}
                            </button>
                          )}
                          {!isEdit && (
                            <p className="text-xs text-gray-500 mt-2">
                              Save the endpoint first before generating an example
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      className="form-textarea w-full text-smd"
                      style={{ minHeight: '100px', width: '100%' }}
                      defaultValue={selectedEndpoint?.description}
                      required
                      placeholder="Describe when and how the LLM should use this endpoint"
                      disabled={hasReadOnlyAccess}
                      readOnly={hasReadOnlyAccess}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label flex items-center">
                      <input
                        type="checkbox"
                        checked={isSessionStart}
                        onChange={(e) => setIsSessionStart(e.target.checked)}
                        className="form-checkbox"
                        disabled={hasReadOnlyAccess}
                      />
                      <span className="">Set as Session Start API</span>
                    </label>
                    <p className="text-sm text-gray-500 mt-1 ml-6">
                      This API will be called when a chat session begins to retrieve user information.
                    </p>
                  </div>

                  {/* Error Response Mapping Section */}
                  <div className="form-section mt-6">
                    <h3 className="text-lg font-medium mb-4 text-red-600">⚠️ Error Response Mapping</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Define how to extract error information when the API call fails. This helps create meaningful error responses for users.
                    </p>
                    <div className="space-y-4">
                      {errorResponseMappings.map((mapping) => (
                        <div key={mapping.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Variable Name
                              </label>
                              <input
                                type="text"
                                className="form-input text-smd"
                                placeholder="error_message"
                                value={mapping.key}
                                onChange={(e) => updateErrorResponseMapping(mapping.id, 'key', e.target.value)}
                                disabled={hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                JSON Path
                              </label>
                              <input
                                type="text"
                                className="form-input text-smd"
                                placeholder="error.message"
                                value={mapping.path}
                                onChange={(e) => updateErrorResponseMapping(mapping.id, 'path', e.target.value)}
                                disabled={hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Data Type
                              </label>
                              <select
                                className="api-manager-form-select text-smd"
                                value={mapping.type || 'string'}
                                onChange={(e) => updateErrorResponseMapping(mapping.id, 'type', e.target.value)}
                                disabled={hasReadOnlyAccess}
                              >
                                <option value="string">String</option>
                                <option value="number">Number</option>
                                <option value="boolean">Boolean</option>
                                <option value="object">Object</option>
                                <option value="array">Array</option>
                              </select>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Error Condition
                              </label>
                              <select
                                className="api-manager-form-select text-smd"
                                value={mapping.errorCondition || 'always'}
                                onChange={(e) => updateErrorResponseMapping(mapping.id, 'errorCondition', e.target.value)}
                                disabled={hasReadOnlyAccess}
                              >
                                <option value="always">Always extract on error</option>
                                <option value="status_code">HTTP Status Code</option>
                                <option value="response_field">Response Field Value</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Condition Value
                              </label>
                              <input
                                type="text"
                                className="form-input text-smd"
                                placeholder={mapping.errorCondition === 'status_code' ? '400-499 or 500' : 'error_value'}
                                value={mapping.errorValue || ''}
                                onChange={(e) => updateErrorResponseMapping(mapping.id, 'errorValue', e.target.value)}
                                disabled={mapping.errorCondition === 'always' || hasReadOnlyAccess}
                                readOnly={hasReadOnlyAccess}
                              />
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Description
                            </label>
                            <input
                              type="text"
                              className="form-input text-smd w-full"
                              placeholder="Description of this error mapping"
                              value={mapping.description || ''}
                              onChange={(e) => updateErrorResponseMapping(mapping.id, 'description', e.target.value)}
                              disabled={hasReadOnlyAccess}
                              readOnly={hasReadOnlyAccess}
                            />
                          </div>

                          {!hasReadOnlyAccess && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm mt-4 text-smd"
                              onClick={() => removeErrorResponseMapping(mapping.id)}
                            >
                              Remove Error Mapping
                            </button>
                          )}
                        </div>
                      ))}
                      {!hasReadOnlyAccess && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm text-smd mb-2"
                          onClick={addErrorResponseMapping}
                        >
                          Add Error Response Mapping
                        </button>
                      )}
                      
                      {errorResponseMappings.length === 0 && (
                        <div className="text-center p-6 border-2 border-dashed border-red-300 rounded-lg bg-red-50">
                          <p className="text-red-600 text-sm mb-2">
                            <strong>No error mappings configured</strong>
                          </p>
                          <p className="text-gray-500 text-xs">
                            Error mappings help extract meaningful error information when API calls fail.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {hasReadOnlyAccess && (
                    <ReadOnlyIndicator />
                  )}

                  <div className="flex gap-2">
                    {canManageApiEndpoints && (
                      <button 
                        type="submit" 
                        className="btn btn-primary text-smd"
                        disabled={isSaving}
                      >
                        {isSaving ? 'Saving...' : selectedEndpoint ? 'Update Endpoint' : 'Create Endpoint'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary text-smd ml-2"
                      onClick={() => {
                        setIsFormOpen(false);
                        setSelectedEndpoint(null);
                        setIsSessionStart(false);
                        setError(null);
                      }}
                    >
                      {canManageApiEndpoints ? 'Cancel' : 'Close'}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        )}

        <div className="card mt-4">
          <h2 className="text-lg font-medium mb-4">API Endpoints</h2>
          {isLoading ? (
            <p>Loading endpoints...</p>
          ) : isError ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p><strong>Error loading endpoints:</strong> {fetchError?.message}</p>
              {debugInfo && (
                <div className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-60">
                  <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
                </div>
              )}
              <button 
                className="mt-2 btn btn-sm btn-primary"
                onClick={() => queryClient.invalidateQueries(['apiEndpoints', projectId])}
              >
                Retry Loading
              </button>
            </div>
          ) : isEmpty(endpoints) ? (
            <div>
              <p>No API endpoints configured yet. Add one to get started!</p>
              <div className="mt-4 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-700">
                  <strong>Debug Info:</strong> Make sure you've created endpoints and they are being properly fetched.
                </p>
                <div className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                  <div><strong>Project ID:</strong> {projectId}</div>
                  <div><strong>Endpoints data:</strong></div>
                  <pre>{JSON.stringify(endpoints, null, 2)}</pre>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {endpoints.map((endpoint) => (
                <div key={endpoint._id || Math.random().toString()} className="border rounded p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">{endpoint.name}</h3>
                      <p className="text-smd text-gray-600">{endpoint.description.length > 250
                        ? `${endpoint.description.slice(0, 250)}...`
                        : endpoint.description}</p>
                      <div className="mt-2 space-x-2">
                        <span className={`badge ${endpoint.endpointType === 'internal' ? 'badge-success' : 'badge-primary'}`}>
                          {endpoint.endpointType === 'internal' ? 'Internal API' : 'External API'}
                        </span>
                        {endpoint.endpointType === 'internal' ? (
                          <span className="badge badge-info">
                            {endpoint.internalApiType === 'nearest' ? 'Find Nearest Locations' : 
                             endpoint.internalApiType === 'geocode' ? 'Geocode Address' : 
                             endpoint.internalApiType === 'reverse-geocode' ? 'Reverse Geocode Coordinates' : 
                             endpoint.internalApiType}
                          </span>
                        ) : (
                          <>
                            <span className="badge badge-secondary">{endpoint.method}</span>
                            <span className="badge badge-secondary">{endpoint.url}</span>
                            {endpoint.authType !== 'none' && (
                              <span className="badge badge-warning">{endpoint.authType}</span>
                            )}
                          </>
                        )}
                        {(() => {
                          // Use the same logic as in the radio buttons for consistency
                          const currentSessionStartId = sessionStartEndpoint || (projectSettings?.sessionStartEndpointId || null);
                          const isSessionStartEndpoint = Boolean(currentSessionStartId) && 
                                                     endpoint._id && 
                                                     currentSessionStartId.toString() === endpoint._id.toString();
                          
                          return isSessionStartEndpoint ? (
                            <span className="badge badge-success">Session Start API</span>
                          ) : null;
                        })()}
                        {endpoint.responseExample && (
                          <span className="badge badge-info">Has Example Response</span>
                        )}
                      </div>
                    </div>
                    <div className="space-x-2">
                      {shouldShowSyncButton() && permissions.canSyncToProduction() && (
                        <button
                          className="btn btn-sm btn-primary text-smd mb-2"
                          style={{width: '100px'}}
                          onClick={(e) => {
                            console.log('[ApiManager] Sync button click event:', e);
                            console.log('[ApiManager] Endpoint being synced:', endpoint);
                            handleSyncToProduction(endpoint);
                          }}
                        >
                          🚀 Sync
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-secondary text-smd mb-2"
                        style={{width: '100px'}}
                        onClick={() => {
                          setSelectedEndpoint(endpoint);
                          setEndpointType(endpoint.endpointType);
                          setAuthType(endpoint.authType || 'none');
                          setBodyType(endpoint.bodyType || 'none');
                          
                          // Ensure we compare strings to avoid object reference comparison issues
                          const currentSessionStartId = sessionStartEndpoint || (projectSettings?.sessionStartEndpointId || null);
                          const isSelected = Boolean(currentSessionStartId) && 
                                          endpoint._id && 
                                          currentSessionStartId.toString() === endpoint._id.toString();
                          setIsSessionStart(isSelected);
                          
                          setIsFormOpen(true);
                        }}
                      >
                        {canManageApiEndpoints ? 'Edit' : 'View'}
                      </button>
                      {canManageApiEndpoints && (
                        <button
                          className="btn btn-sm btn-danger text-smd"
                          style={{width: '100px'}}
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this endpoint?')) {
                              deleteEndpoint(endpoint._id);
                            }
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sync to Production Modal */}
        {showSyncModal && (
          <div 
            id="sync-modal-backdrop"
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ 
              zIndex: 9999,
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => {
              console.log('[ApiManager] Modal backdrop clicked');
              if (e.target === e.currentTarget) {
                handleCancelSync();
              }
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '28rem',
                margin: '0 1rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                position: 'relative',
                zIndex: 10000
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">🚀 Sync API Endpoint to Production</h3>
              
              {syncResult ? (
                <div className="text-center">
                  <div className="text-green-600 text-2xl mb-2">✅</div>
                  <h4 className="text-lg font-medium text-green-800 mb-2">Sync Successful!</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Endpoint:</strong> {syncResult.sync?.endpointName}</p>
                    <p><strong>Type:</strong> {syncResult.sync?.endpointType}</p>
                    {syncResult.sync?.parametersCount > 0 && (
                      <p><strong>Parameters:</strong> {syncResult.sync.parametersCount}</p>
                    )}
                    {syncResult.sync?.urlUpdated && (
                      <p><strong>Production URL:</strong> {syncResult.sync.urlUpdated}</p>
                    )}
                    <p><strong>Synced:</strong> {new Date(syncResult.sync?.syncedAt).toLocaleString()}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">This modal will close automatically...</p>
                </div>
              ) : (
                <>
                  <div className="text-sm text-gray-700 mb-4">
                    <p className="mb-2">You are about to sync the following API endpoint to production:</p>
                    <div className="bg-gray-50 p-3 rounded border">
                      <p><strong>Name:</strong> {endpointToSync?.name}</p>
                      <p><strong>Type:</strong> {endpointToSync?.endpointType} 
                        {endpointToSync?.internalApiType && ` (${endpointToSync.internalApiType})`}
                      </p>
                      <p><strong>Method:</strong> {endpointToSync?.method}</p>
                      {endpointToSync?.url && (
                        <p><strong>URL:</strong> {endpointToSync.url}</p>
                      )}
                      {endpointToSync?.parameters?.length > 0 && (
                        <p><strong>Parameters:</strong> {endpointToSync.parameters.length}</p>
                      )}
                      {endpointToSync?.authType !== 'none' && (
                        <p><strong>Auth:</strong> {endpointToSync.authType}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-orange-800">Important</h3>
                        <div className="mt-2 text-sm text-orange-700">
                          <p>This will completely overwrite the API endpoint in production with the current staging version.</p>
                          {endpointToSync?.endpointType === 'internal' && (
                            <p className="mt-1"><strong>Note:</strong> Internal API URLs will be automatically updated for production.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      <strong>Error:</strong> {error}
                    </div>
                  )}

                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={handleCancelSync}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                      disabled={syncLoading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmSync}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      disabled={syncLoading}
                    >
                      {syncLoading ? 'Syncing...' : 'Confirm Sync'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ApiManager; 